import type { LiveSnapshot } from '@/types/live';

// The SWR fetcher both stage views use. `/api/:eventSlug/live` is polled every 3s and its
// body is the same one the controller first-painted from (PORTING.md U4).
export const fetchSnapshot = async (url: string): Promise<LiveSnapshot> => {
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) throw new Error('Failed to load');
  return (await res.json()) as LiveSnapshot;
};

export function extractGithubHandle(github: string | null): string | null {
  if (!github) return null;
  const trimmed = github.trim();
  if (!trimmed) return null;
  if (trimmed.includes('github.com')) {
    const parts = trimmed.split('/').filter(Boolean);
    return parts[parts.length - 1] ?? null;
  }
  return trimmed.replace(/^@/, '');
}

export function githubUrl(github: string | null): string | null {
  const handle = extractGithubHandle(github);
  return handle ? `https://github.com/${handle}` : null;
}

// The stream URL as an embeddable one: youtu.be short links, `?v=` watch links and
// `/live/<id>` permalinks all become /embed/<id>?autoplay=1; anything else is used as-is.
export function toEmbedUrl(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const url = new URL(raw);
    if (url.hostname === 'youtu.be') {
      const id = url.pathname.replace('/', '');
      return id ? `https://www.youtube.com/embed/${id}?autoplay=1` : null;
    }
    if (url.hostname.endsWith('youtube.com')) {
      if (url.pathname.startsWith('/embed/')) return raw;
      const v = url.searchParams.get('v');
      if (v) return `https://www.youtube.com/embed/${v}?autoplay=1`;
      if (url.pathname.startsWith('/live/')) {
        const id = url.pathname.replace('/live/', '').split('/')[0];
        return id ? `https://www.youtube.com/embed/${id}?autoplay=1` : null;
      }
    }
    return raw;
  } catch {
    return null;
  }
}

export function initialsFor(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}
